import InputField from "components/fields/InputField";
import { FcGoogle } from "react-icons/fc";
import Checkbox from "components/checkbox";
import { useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";

export default function SignIn() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;
    console.log("Input changed:", id, value); // Debug log
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear error when user types
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username or Email is required";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted with:", formData);

    if (!validateForm()) {
      console.log("Validation failed", errors);
      return;
    }

    setIsLoading(true);
    try {
      console.log("Making API call with:", formData);
      const response = await axios.post(' https://ksa-manager-back.onrender.com/login', {
        username_or_email: formData.username,
        password: formData.password
      });
      console.log("API Response:", response.data);
      
      if (response.data.message === "Login successful") {
        // Store the entire response data in localStorage
        localStorage.setItem('ksa_user', JSON.stringify(response.data));
        toast.success('Login successful!');
        navigate('/admin/warehouse');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-navy-900 py-12 px-4 sm:px-6 lg:px-8">
      <Toaster position="top-right" />
      
      {/* Main container with two columns */}
      <div className="max-w-7xl w-full flex rounded-2xl shadow-2xl overflow-hidden">
        {/* Left column - Image section */}
        <div className="hidden lg:block lg:w-1/2 relative">
          {/* Hero image */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-green-800/90 z-10" />
          <img 
            src="https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?ixlib=rb-4.0.3"
            alt="Sports Academy"
            className="w-full h-full object-cover"
          />
          {/* Overlay text */}
          <div className="absolute inset-0 flex flex-col justify-center items-center z-20 text-white p-8">
            <h2 className="text-5xl font-bold mb-4">Kempshot Sports Academy</h2>
            <p className="text-xl text-center">Developing Champions Since 2024</p>
            
            {/* Vision Statement */}
            <div className="mt-8 bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <p className="italic text-lg">"Our mission is to nurture young talent and create future champions through world-class training, state-of-the-art facilities, and dedicated coaching."</p>
              <p className="mt-2 font-semibold">- Kempshot Sports Academy Vision</p>
            </div>

            {/* Features */}
            <div className="mt-8 grid grid-cols-2 gap-4 w-full max-w-md">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                <p className="font-bold text-2xl">New</p>
                <p className="text-sm">Launched 2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                <p className="font-bold text-2xl">50+</p>
                <p className="text-sm">Active Players</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Sign in form */}
        <div className="w-full lg:w-1/2 bg-white dark:bg-navy-800 p-8 lg:p-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h4 className="text-3xl font-bold text-green-700 dark:text-green-500 mb-2">
                Welcome Back
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Sign in to access your academy dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username/Email field */}
              <InputField
                variant="auth"
                extra="mb-3"
                label="Username or Email*"
                placeholder="Enter your username or email"
                id="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                error={errors.username}
                required
                className="dark:bg-navy-900"
              />

              {/* Password with toggle */}
              <div className="relative">
                <InputField
                  variant="auth"
                  extra="mb-3"
                  label="Password*"
                  placeholder="Min. 8 characters"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  required
                  className="dark:bg-navy-900"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[50px] transform -translate-y-1/4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Remember me and Forgot password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox />
                  <p className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Remember me
                  </p>
                </div>
                <a
                  className="text-sm font-medium text-green-600 hover:text-green-700 dark:text-green-500"
                  href="#"
                >
                  Forgot Password?
                </a>
              </div>

              {/* Sign in button */}
              <button 
                type="submit" 
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-medium hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
              >
                {isLoading ? (
                  <ClipLoader size={20} color="#ffffff" />
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Additional features */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                New to Kempshot Sports Academy?{' '}
                <a href="#" className="font-medium text-green-600 hover:text-green-700 dark:text-green-500">
                  Register now
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
